import React, {useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {BasicButton} from '../../widgets/components/UI/BasicButton'
import TagManager from 'react-gtm-module'
import {GTMEvent} from '../../../hooks/gtmHelpers'
import {useAuth} from '../../auth'
import {disconnect2Fa, forgotPassword, get2FaDetails} from '../../auth/core/_requests'
import {ChangePasswordModal} from '../../auth/components/ChangePasswordModal'
import TwoFAModal from './TwoFAModal'
import {toast} from 'react-toastify'
import DisconnectTwoFAModal from './settings/DisconnectTwoFAModal'
// import {useTimeZone} from '../../../hooks/useTimeZone'
import {useDateFormat} from '../../../hooks/useDateFormat'
import {DateTime} from 'luxon'

const Security = ({key}: {key: number}) => {
  const {formatMessage} = useIntl()
  const {companyId, currentUser, setCurrentUser} = useAuth() //eslint-disable-line
  const [showModal, setShowModal] = useState(false)
  const [isActive, setIsActive] = useState(false)
  const [authDone, setAuthDone] = useState(false) //eslint-disable-line
  const [changePasswordLoading, setChangePasswordLoading] = useState(false)
  const [, setHasErrors] = useState<boolean | undefined>()
  const [twoFaDetails, setTwoFaDetails] = useState<any>()
  const [isDisconnecting, setIsDisconnecting] = useState(false) //eslint-disable-line
  const [isLoading, setIsLoading] = useState(false) //eslint-disable-line
  const [isDisconnecting2Fa, setIsDisconnecting2Fa] = useState<boolean>(false)
  // const {getTimeZoneValue} = useTimeZone()
  const {getDateValue} = useDateFormat()

  const getForgotedPassword = async () => {
    if (currentUser?.email) {
      try {
        setChangePasswordLoading(true)
        const {
          data: {success},
        } = await forgotPassword(currentUser?.email)
        if (success) {
          setShowModal(true)
          setHasErrors(false)
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.ForgotPasswordSuccessful,
              action: 'normal',
              metadata: {
                email: currentUser?.email,
                user_id: currentUser?.userId,
                company_id: companyId,
              },
            },
          })
        } else {
          setHasErrors(true)
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.ForgotPasswordFailed,
              action: 'normal',
              metadata: {
                email: currentUser?.email,
                user_id: currentUser?.userId,
                company_id: companyId,
              },
            },
          })
        }
      } catch (error) {
        console.log(error)
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.ForgotPasswordFailed,
            action: 'normal',
            metadata: {
              email: currentUser?.email,
              user_id: currentUser?.userId,
              company_id: companyId,
            },
          },
        })
      } finally {
        setChangePasswordLoading(false)
      }
    }
  }

  const get2FADetails = async () => {
    setIsLoading(true)
    try {
      const {
        data: {success, data},
      } = await get2FaDetails()
      if (success) {
        setTwoFaDetails(data)
        setAuthDone(data?.twoFa)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    get2FADetails()
  }, [companyId, isActive, key])

  const disconnectTwoFa = async () => {
    setIsDisconnecting(true)
    try {
      const {
        data: {success},
      } = await disconnect2Fa()
      if (success) {
        get2FADetails()
        toast.success(formatMessage({id: '2FA turned off successfully'}))
      }
    } catch (err) {
      console.log(err)
    } finally {
      setIsDisconnecting(false)
      setIsDisconnecting2Fa(false)
    }
  }

  return (
    <div className='card rounded-1 p-6 d-flex gap-6'>
      <div className='d-flex flex-md-row flex-column justify-content-between align-items-center gap-4'>
        <div className='w-md-75 w-100'>
          <p className='fw-bold mb-0'>{formatMessage({id: 'Update Password'})}</p>
          <p className='mb-0'>
            {formatMessage({
              id: 'To keep your account secure, we recommend updating your password regularly. Choose a strong password with a mix of uppercase and lowercase letters, numbers, and special characters to enhance security.',
            })}
          </p>
        </div>
        <div className='w-md-25 w-100 d-flex justify-content-end'>
          {/* <button className='btn bg-primary font-size-13 text-white custom-button-property w-md-75 w-100'>{formatMessage({ id: 'Change Password' })}</button> */}
          <BasicButton
            buttonText={formatMessage({id: 'Change password'})}
            onClick={getForgotedPassword}
            disabled={changePasswordLoading}
            loading={changePasswordLoading}
            height='44px'
            customClass='d-flex align-items-center justify-content-center bg-primary text-white w-md-75 w-100'
            padding='12px 24px'
          />
        </div>
      </div>
      <div className='d-flex flex-md-row flex-column justify-content-between align-items-center gap-4'>
        <div className='w-md-75 w-100'>
          <p className='fw-bold mb-0'>{formatMessage({id: 'Two-factor authentication'})}</p>
          <p className='mb-0'>
            {formatMessage({
              id: 'Protect your account from unauthorized access by enabling two-factor authentication (2FA). Once enabled, you’ll be required to provide identification when logging into RecordsKeeper.AI Dashboard.',
            })}
          </p>
        </div>
        <div className='w-md-25 w-100'>
          {isLoading ? (
            <div className='d-flex flex-column w-100 align-items-end'>
              <BasicButton
                buttonText={formatMessage({id: 'Please wait...'})}
                disabled={true}
                height='44px'
                customClass='bg-secondary text-dark w-md-75 w-100'
                padding='12px 24px'
              />
            </div>
          ) : (
            <>
              {!authDone ? (
                <div className='d-flex flex-column w-100 align-items-end'>
                  {/* <button className='btn bg-primary font-size-13 text-white custom-button-property w-md-75 w-100'>{formatMessage({ id: 'Turn on 2FA' })}</button> */}
                  <BasicButton
                    buttonText={formatMessage({id: 'Turn on 2FA'})}
                    onClick={() => setIsActive(true)}
                    // disabled={changePasswordLoading}
                    // loading={changePasswordLoading}
                    height='44px'
                    customClass='d-flex align-items-center justify-content-center bg-primary text-white w-md-75 w-100'
                    padding='12px 24px'
                  />
                </div>
              ) : (
                <div className='d-flex flex-column w-100 align-items-end'>
                  {/* <button className='btn bg-danger font-size-13 text-white custom-button-property w-md-75 w-100'>{formatMessage({ id: 'Turn off 2FA' })}</button> */}
                  <BasicButton
                    buttonText={formatMessage({id: 'Turn off 2FA'})}
                    onClick={() => setIsDisconnecting2Fa(true)}
                    disabled={isDisconnecting}
                    height='44px'
                    customClass='bg-danger text-white w-md-75 w-100'
                    padding='12px 24px'
                  />
                  <div className='d-flex w-75 justify-content-center pt-2'>
                    <p className='mb-0 font-size-12'>
                      {formatMessage({id: 'Activated on: '})}
                      {twoFaDetails && twoFaDetails?.twoFaVerifiedAt
                        ? getDateValue(twoFaDetails?.twoFaVerifiedAt)
                          ? getDateValue(twoFaDetails?.twoFaVerifiedAt)
                          : DateTime.fromISO(twoFaDetails?.twoFaVerifiedAt).toLocaleString(
                              DateTime.DATE_MED
                            )
                        : '-'}{' '}
                      {/* {twoFaDetails?.twoFaVerifiedAt && getTimeZoneValue(twoFaDetails?.twoFaVerifiedAt)} */}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <ChangePasswordModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        setShowModal={setShowModal}
      />
      <TwoFAModal isActive={isActive} setIsActive={setIsActive} />
      <DisconnectTwoFAModal
        isActive={isDisconnecting2Fa}
        setIsActive={setIsDisconnecting2Fa}
        disconnect2Fa={disconnectTwoFa}
        isDisconnecting={isDisconnecting}
      />
    </div>
  )
}

export default Security
